import { useState, useEffect, useCallback } from 'react';
import { Download, BarChart3, TrendingUp, Calendar, HandCoins } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function getInitials(name) {
  return name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'AG';
}

function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

export default function OwnerReports() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const isManager = currentUser.role === 'manager';
  const [view, setView] = useState(isManager ? 'recovery' : 'acquisition');
  const [performance, setPerformance] = useState([]);
  const [recovery, setRecovery] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (isManager) {
        const recRes = await api.get(`/reports/recovery-performance?period=${period}`).catch(() => ({ data: [] }));
        setPerformance([]);
        setDailyTrend([]);
        setRecovery(recRes.data || []);
      } else {
        const [perfRes, trendRes, recRes] = await Promise.all([
          api.get(`/reports/performance?period=${period}`),
          api.get('/reports/daily-trend'),
          api.get(`/reports/recovery-performance?period=${period}`).catch(() => ({ data: [] })),
        ]);
        setPerformance(perfRes.data || []);
        setDailyTrend(trendRes.data || []);
        setRecovery(recRes.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period, isManager]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/reports/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `merchants_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const totals = performance.reduce((acc, a) => ({
    total: acc.total + a.total,
    onboarded: acc.onboarded + a.onboarded,
    upiQr: acc.upiQr + (a.upiQr || 0),
    followupsDone: acc.followupsDone + (a.followupsDone || 0)
  }), { total: 0, onboarded: 0, upiQr: 0, followupsDone: 0 });

  // Weighted performance score (industry practice):
  // 40% Registration (merchants acquired)
  // 30% Engagement (follow-ups/revisits done)
  // 30% Conversion (merchants onboarded)
  const calcPerformance = (agent) => {
    const maxReg = Math.max(...performance.map(a => a.total), 1);
    const maxFu = Math.max(...performance.map(a => a.followupsDone || 0), 1);
    const regScore = (agent.total / maxReg) * 40;
    const fuScore = ((agent.followupsDone || 0) / maxFu) * 30;
    const convScore = (agent.conversion / 100) * 30;
    return Math.min(100, Math.round(regScore + fuScore + convScore));
  };

  const maxTrend = Math.max(...dailyTrend.map(d => d.count), 1);

  return (
    <OwnerLayout title="Reports">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Performance Reports</h2>
          <p className="text-sm text-gray">{isManager ? 'Recovery KPIs for your branch' : 'Agent comparison & data export'}</p>
        </div>
        {!isManager && (
          <button
            className="btn-navy"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            <Download size={14} style={{ marginRight: '4px' }} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )}
      </div>

      {/* Period Tabs */}
      <div className="flex items-center gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
        {!isManager && (
          <>
            <span className="text-sm text-gray mr-2">View:</span>
            <div className="period-tabs">
              <button className={`period-tab ${view === 'acquisition' ? 'active' : ''}`} onClick={() => setView('acquisition')}>
                <BarChart3 size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Acquisition
              </button>
              <button className={`period-tab ${view === 'recovery' ? 'active' : ''}`} onClick={() => setView('recovery')}>
                <HandCoins size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Recovery
              </button>
            </div>
          </>
        )}
        <span className="text-sm text-gray mr-2" style={{ marginLeft: isManager ? 0 : '12px' }}>Period:</span>
        <div className="period-tabs">
          {['week', 'month', 'all'].map(p => (
            <button
              key={p}
              className={`period-tab ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
              data-testid={`period-${p}`}
            >
              {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'All Time'}
            </button>
          ))}
        </div>
      </div>

      {/* Daily Trend */}
      {view === 'acquisition' && (
      <div className="panel mb-4 fu">
        <div className="panel-header">
          <span className="panel-title">
            <TrendingUp size={14} style={{ marginRight: '6px' }} />
            Daily Registration Trend (Last 14 Days)
          </span>
        </div>
        <div className="p-4">
          <div className="flex items-end gap-1" style={{ height: '120px' }}>
            {dailyTrend.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full rounded-t transition-all"
                  style={{
                    height: `${Math.max(4, (d.count / maxTrend) * 100)}px`,
                    background: d.count > 0 ? 'var(--navy)' : 'var(--g2)',
                    minHeight: '4px'
                  }}
                  title={`${d.date}: ${d.count} registrations`}
                />
                <div className="text-xs text-gray mt-1" style={{ fontSize: '9px' }}>{d.dayOfWeek}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Team Comparison Table (Acquisition) */}
      {view === 'acquisition' && (
      <div className="panel fu-1">
        <div className="panel-header">
          <span className="panel-title">Team Comparison</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ctable">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Registered</th>
                  <th>Follow-ups</th>
                  <th>Onboarded</th>
                  <th>Performance</th>
                  <th>Conversion</th>
                </tr>
              </thead>
              <tbody>
                {performance.map(agent => (
                  <tr key={agent.id} data-testid={`report-row-${agent.id}`}>
                    <td>
                      <span className="font-heading font-bold text-navy">#{agent.rank}</span>
                    </td>
                    <td>
                      <div className="t-agent">
                        <div className="t-av">{getInitials(agent.name)}</div>
                        <div>
                          <div className="t-name">{agent.name}</div>
                          <div className="t-id">{agent.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${agent.isActive ? 'active' : 'off'}`}>
                        {agent.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className="n-val big">{agent.total}</span>
                    </td>
                    <td>
                      <span className="n-val">{agent.followupsDone || 0}</span>
                    </td>
                    <td>
                      <span className="n-val">{agent.onboarded}</span>
                    </td>
                    <td>
                      {(() => {
                        const score = calcPerformance(agent);
                        const color = score >= 70 ? '#16a34a' : score >= 40 ? '#d97706' : '#dc2626';
                        return (
                          <div className="bar-wrap">
                            <div className="bar-track">
                              <div className="bar-fill" style={{ width: `${score}%`, background: color }} />
                            </div>
                            <span className="bar-pct" style={{ color }}>{score}%</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <span className="n-val">{agent.conversion}%</span>
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="totals">
                  <td></td>
                  <td>
                    <div className="t-agent">
                      <div className="t-av navy">Σ</div>
                      <div>
                        <div className="t-name">Team Total</div>
                        <div className="t-id">{performance.length} agents</div>
                      </div>
                    </div>
                  </td>
                  <td>—</td>
                  <td><span className="n-val big">{totals.total}</span></td>
                  <td><span className="n-val">{totals.followupsDone}</span></td>
                  <td><span className="n-val">{totals.onboarded}</span></td>
                  <td>—</td>
                  <td>
                    <span className="n-val">
                      {totals.total > 0 ? Math.round((totals.onboarded / totals.total) * 100) : 0}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Recovery Performance */}
      {view === 'recovery' && (
        <RecoveryPerformance rows={recovery} loading={loading} />
      )}
    </OwnerLayout>
  );
}

function RecoveryPerformance({ rows, loading }) {
  if (loading) {
    return <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
        No recovery activity yet. Once managers and agents start handling cases, their KPIs will appear here.
      </div>
    );
  }

  const managers = rows.filter((r) => r.role === 'manager');
  const agents = rows.filter((r) => r.role === 'agent');

  // Avoid double-counting when multiple managers share the same branch (each
  // manager row repeats the same branch totals). For the headline KPIs we keep
  // only one manager per branch when summing case/overdue, but ALL rows still
  // contribute their own payments / PTPs / actions (those are per-user).
  const seenBranches = new Set();
  const dedupedForTotals = rows.filter((r) => {
    if (r.role !== 'manager') return true;
    const key = r.branchId || `__no_branch__${r.id}`;
    if (seenBranches.has(key)) return false;
    seenBranches.add(key);
    return true;
  });

  const totals = rows.reduce((acc, r) => ({
    // cases & overdue come from deduped set (branch-distinct for managers)
    cases: acc.cases,
    overdue: acc.overdue,
    // PTPs, payments, actions are per-user — sum all rows
    ptps: acc.ptps + r.ptpsCreated,
    missed: acc.missed + r.ptpsMissed,
    payments: acc.payments + r.paymentsCount,
    collected: acc.collected + r.paymentsAmount,
    actions: acc.actions + r.actionsLogged,
  }), { cases: 0, overdue: 0, ptps: 0, missed: 0, payments: 0, collected: 0, actions: 0 });
  totals.cases = dedupedForTotals.reduce((s, r) => s + r.casesAssigned, 0);
  totals.overdue = dedupedForTotals.reduce((s, r) => s + r.totalOverdue, 0);

  return (
    <>
      {/* Summary tiles */}
      <div className="od-stats" style={{ marginBottom: '16px' }}>
        <div className="od-stat">
          <div className="od-stat-lbl">Active Cases</div>
          <div className="od-stat-val">{totals.cases}</div>
          <div className="od-stat-sub">Across {rows.length} users</div>
        </div>
        <div className="od-stat accent">
          <div className="od-stat-lbl">Outstanding</div>
          <div className="od-stat-val red">{fmtINR(totals.overdue)}</div>
          <div className="od-stat-sub">Total overdue</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">Collected</div>
          <div className="od-stat-val" style={{ color: '#16a34a' }}>{fmtINR(totals.collected)}</div>
          <div className="od-stat-sub">{totals.payments} payments</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">PTP Keep Rate</div>
          <div className="od-stat-val">{totals.ptps > 0 ? Math.round(((totals.ptps - totals.missed) / totals.ptps) * 100) : 0}%</div>
          <div className="od-stat-sub">{totals.ptps} PTPs · {totals.missed} missed</div>
        </div>
      </div>

      {managers.length > 0 && (
        <div className="panel mb-4">
          <div className="panel-header"><span className="panel-title">Managers</span></div>
          <KpiTable rows={managers} />
        </div>
      )}
      {agents.length > 0 && (
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Field Agents</span></div>
          <KpiTable rows={agents} />
        </div>
      )}
    </>
  );
}

function KpiTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="ctable">
        <thead>
          <tr>
            <th>User</th>
            <th>Branch</th>
            <th>Cases</th>
            <th>Outstanding</th>
            <th>PTPs</th>
            <th>Missed</th>
            <th>Keep %</th>
            <th>Payments</th>
            <th>Collected</th>
            <th>Recovery %</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const keepColor = r.ptpKeepRate >= 70 ? '#16a34a' : r.ptpKeepRate >= 40 ? '#d97706' : '#dc2626';
            const recColor = r.recoveryRate >= 30 ? '#16a34a' : r.recoveryRate >= 10 ? '#d97706' : '#dc2626';
            return (
              <tr key={r.id}>
                <td>
                  <div className="t-agent">
                    <div className="t-av">{getInitials(r.name)}</div>
                    <div>
                      <div className="t-name">{r.name}</div>
                      <div className="t-id">{r.employeeId}{!r.recoveryEnabled && <span style={{ color: 'var(--red)' }}> · recovery off</span>}</div>
                    </div>
                  </div>
                </td>
                <td><span className="text-xs text-gray">{r.branchName || '—'}</span></td>
                <td><span className="n-val big">{r.casesAssigned}</span></td>
                <td><span className="n-val" style={{ color: 'var(--red)' }}>{fmtINR(r.totalOverdue)}</span></td>
                <td><span className="n-val">{r.ptpsCreated}</span></td>
                <td><span className="n-val" style={{ color: r.ptpsMissed > 0 ? 'var(--red)' : 'var(--g3)' }}>{r.ptpsMissed}</span></td>
                <td><span className="n-val" style={{ color: keepColor, fontWeight: 700 }}>{r.ptpsCreated > 0 ? `${r.ptpKeepRate}%` : '—'}</span></td>
                <td><span className="n-val">{r.paymentsCount}</span></td>
                <td><span className="n-val" style={{ color: '#16a34a', fontWeight: 700 }}>{fmtINR(r.paymentsAmount)}</span></td>
                <td><span className="n-val" style={{ color: recColor, fontWeight: 700 }}>{r.recoveryRate}%</span></td>
                <td><span className="n-val">{r.actionsLogged}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
